import { useMemo } from "react";
import { useStores } from "@hooks";
import { isProjectObserver, isProjectOwner } from "@utils";

export const useCheckEstimateEditorAccess = (
  estimateUsers = undefined,
  workTypesWithStatus = undefined
) => {
  const { userStore } = useStores();
  const {
    userId,
    userUuid,
    isAdmin,
    isManager,
    isBusinessUser,
    isMiniUser
  } = userStore;

  const isContractor = useMemo(
    () => Boolean((estimateUsers?.contractor || []).filter(Boolean).find((o) => o.id === userId)),
    [ userId, estimateUsers ]
  );

  const isOwner = useMemo(
    () => !isContractor && isProjectOwner(userId, estimateUsers, isManager),
    [ isContractor, isManager, userId, estimateUsers ]
  );

  const isObserver = useMemo(
    () => !isContractor && isProjectObserver(userId, estimateUsers, isManager),
    [ isContractor, isManager, userId, estimateUsers ]
  );

  const hasSharePrivileges = useMemo(
    () => !isContractor && (isManager || (isBusinessUser && isObserver)),
    [ isContractor, isManager, isBusinessUser, isObserver ]
  );

  const hasSellerPrivileges = useMemo(
    () => !isContractor && (isManager || isBusinessUser),
    [ isContractor, isManager, isBusinessUser ]
  );

  const hasEditorPrivileges = useMemo(
    () => !isMiniUser && (isObserver || isContractor),
    [ isContractor, isMiniUser, isObserver ]
  );

  const userGrantedPermits = useMemo(() => {
    if (!userUuid || !workTypesWithStatus?.length) return [];
    return workTypesWithStatus.filter(
      (permit) => permit.estimatorUuid === userUuid
    );
  }, [ workTypesWithStatus ]);

  const hasUserGrantedPermits = useMemo(
    () => Boolean(!isOwner && userGrantedPermits?.length),
    [ isOwner, userGrantedPermits ]
  );
  
  return {
    userUuid,
    userId,
    isOwner,
    isObserver,
    isContractor,
    hasSharePrivileges,
    hasSellerPrivileges,
    hasEditorPrivileges,
    userGrantedPermits,
    hasUserGrantedPermits,

    isAdmin: isAdmin && !isContractor,
    isManager: isManager && !isContractor,
    isMiniUser,
    isBusinessUser: isBusinessUser && !isContractor,
  };
};
